import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import GalleryManager from './GalleryManager';
import AlbumsGalleryAdmin from './AlbumsGalleryAdmin';
import ImageUpload from './ImageUpload';
import TestimonialsAdmin from './TestimonialsAdmin';
import SessionsAdmin from './SessionsAdmin';
import FeaturedProgramsAdmin from './FeaturedProgramsAdmin';
import FeaturesHighlightAdmin from './FeaturesHighlightAdmin';
import ServicesAdmin from './ServicesAdmin';
import ValuesAdmin from './ValuesAdmin';
import TimelineAdmin from './TimelineAdmin';
import ThreeBlocksAdmin from './ThreeBlocksAdmin';
import ExpertsTeamAdmin from './ExpertsTeamAdmin';
import ContactSectionAdmin from './ContactSectionAdmin';
import HeroVideoAdmin from './HeroVideoAdmin';
import PageHeaderAdmin from './PageHeaderAdmin';
import SpecialtiesAdmin from './SpecialtiesAdmin';
import CompanyAdmin from './CompanyAdmin';

const ContentEditor = ({ contentPath }) => {
  const { getContent, updateContent, content } = useContent();
  const [sectionData, setSectionData] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
    
    // Auto-expand items for better UX
    if (contentPath === 'formations.formationsGrid' && data?.formations) {
      const newExpandedItems = {};
      data.formations.forEach((_, index) => {
        newExpandedItems[`${contentPath}.formations.${index}`] = true;
      });
      setExpandedItems(newExpandedItems);
    }
    
    // Auto-expand stats and buttons in hero section
    if (contentPath === 'home.hero') {
      const newExpandedItems = {};
      if (data?.stats) {
        data.stats.forEach((_, index) => {
          newExpandedItems[`${contentPath}.stats.${index}`] = true;
        });
      }
      if (data?.buttons) {
        data.buttons.forEach((_, index) => {
          newExpandedItems[`${contentPath}.buttons.${index}`] = true;
        });
      }
      setExpandedItems(newExpandedItems);
    }

    if (contentPath.endsWith('.cta') && data?.buttons) {
      const newExpandedItems = {};
      data.buttons.forEach((_, index) => {
        newExpandedItems[`${contentPath}.buttons.${index}`] = true;
      });
      setExpandedItems(newExpandedItems);
    }
  }, [contentPath, getContent, content]);

  const handleInputChange = (path, value) => {
    updateContent(path, value);
  };

  const toggleExpanded = (key) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isGalleryArray = (path, key) => {
    // Check if we're editing expertGallery photos or videos from about or experts pages
    return (path.includes('expertGallery') && (key === 'photos' || key === 'videos'));
  };

  const isExpertGallery = (path) => {
    return path.includes('expertGallery');
  };

  const isTestimonialsSection = (path) => {
    return path.includes('Testimonials') && (path.includes('expert') || path.includes('participant'));
  };

  const isUpcomingSessionsSection = (path) => {
    return path.includes('upcomingSessions');
  };

  const isFeaturedProgramsSection = (path) => {
    return path.includes('featuredPrograms');
  };

  const isFeaturesHighlightSection = (path) => {
    return path.includes('featuresHighlight');
  };

  const isServicesSection = (path) => {
    return path.includes('mainServices');
  };

  const isValuesSection = (path) => {
    return path.includes('coreValues');
  };

  const isTimelineSection = (path) => {
    return path.includes('chronologie');
  };

  const isThreeBlocksSection = (path) => {
    return path.includes('threeBlocks');
  };

  const isExpertsTeamSection = (path) => {
    return path.includes('experts.team');
  };

  const isPageHeaderSection = (path) => {
    return path.includes('pageHeader') && 
           (path.includes('about') || 
            path.includes('services') || 
            path.includes('formations') || 
            path.includes('experts') || 
            path.includes('contact') ||
            path.includes('testimonials'));
  };

  const isTestimonialsSubmitSection = (path) => {
    return path.includes('testimonials.submitSection');
  };

  const isContactArraySection = (path) => {
    return path.includes('contact.contactInfo') ||
           path.includes('contact.contactForm') || 
           path.includes('contact.quickActions') || 
           path.includes('contact.trainingLocations') ||
           path.includes('contact.registrationLinks') ||
           path.includes('contact.faq') ||
           path.includes('contact.interactiveMap') ||
           path.includes('contact.socialNetworks') ||
           path.includes('contact.cta');
  };

  const isImageField = (key) => {
    // Check if this is an image field that should use file upload
    const imageFields = [
      'formationPhoto', 'instructorPhoto', 'photo', 'image', 'picture',
      'avatar', 'banner', 'thumbnail', 'logo', 'icon'
    ];
    const lowerKey = key.toLowerCase();
    return imageFields.some(field => lowerKey.includes(field));
  };

  const isVideoField = (key) => {
    return key === 'videoUrl' || key.toLowerCase().includes('video');
  };

  const isHeroSection = (path) => {
    return path === 'home.hero';
  };

  const translateFieldLabel = (key) => {
    const translations = {
      'number': 'Numéro',
      'label': 'Libellé',
      'text': 'Texte',
      'link': 'Lien',
      'type': 'Type',
      'icon': 'Icône',
      'title': 'Titre',
      'subtitle': 'Sous-titre',
      'description': 'Description',
      'videoUrl': 'URL de la vidéo',
      'name': 'Nom',
      'rating': 'Note',
      'comment': 'Commentaire',
      'date': 'Date',
      'location': 'Lieu',
      'spots': 'Places',
      'module': 'Module',
      'duration': 'Durée',
      'content': 'Contenu',
      'address': 'Adresse',
      'phone': 'Téléphone',
      'email': 'E-mail',
      'founded': 'Année de fondation',
      'registrationnumber': "Numéro d'enregistrement",
      'taxnumber': 'Numéro fiscal',
      'tagline': 'Slogan',
      'buttons': 'Boutons',
      'stats': 'Statistiques',
      'target': 'Cible',
      'availability': 'Disponibilité',
      'value': 'Valeur',
      'instructor': 'Instructeur',
      'badge': 'Badge',
      'popular': 'Populaire',
      'question': 'Question',
      'answer': 'Réponse',
      'url': 'URL'
    };
    
    const lowerKey = key.toLowerCase();
    if (translations[lowerKey]) {
      return translations[lowerKey];
    }
    
    // Default: capitalize first letter and add spaces
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
  };

  const getGalleryType = (key) => {
    if (key === 'photos') return 'photos';
    if (key === 'videos') return 'videos';
    return 'mixed';
  };

  // Tabbed Gallery Component
  const TabbedGallery = ({ fullPath, galleryData, onUpdate }) => {
    const defaultTab = galleryData?.defaultTab || 'photos';
    const [activeTab, setActiveTab] = useState(defaultTab);
    
    // Update activeTab when defaultTab changes
    useEffect(() => {
      setActiveTab(defaultTab);
    }, [defaultTab]);
    
    if (!galleryData || typeof galleryData !== 'object') return null;
    
    const photos = galleryData.photos || [];
    const albums = galleryData.albums || [];
    const videos = galleryData.videos || [];
    const videoAlbums = galleryData.videoAlbums || [];
    const tabs = galleryData.tabs || [
      { id: 'photos', icon: 'fas fa-images', text: 'Photos' },
      { id: 'videos', icon: 'fas fa-video', text: 'Vidéos' }
    ];
    
    return (
      <div className="tabbed-gallery">
        {/* Default Tab Selector */}
        <div className="form-group" style={{marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px'}}>
          <label style={{fontWeight: '600', marginBottom: '10px', display: 'block', color: '#374151'}}>
            <i className="fas fa-star" style={{marginRight: '8px', color: '#3b82f6'}}></i>
            Onglet par défaut sur le site
          </label>
          <select
            value={defaultTab}
            onChange={(e) => onUpdate(`${fullPath}.defaultTab`, e.target.value)}
            style={{
              padding: '10px',
              borderRadius: '6px',
              border: '2px solid #e5e7eb',
              fontSize: '14px',
              width: '100%',
              cursor: 'pointer'
            }}
          >
            <option value="photos">📷 Photos (affichées en premier)</option>
            <option value="videos">🎥 Vidéos (affichées en premier)</option>
          </select>
          <small style={{display: 'block', marginTop: '8px', color: '#6b7280'}}>
            Cet onglet sera automatiquement sélectionné lorsque les visiteurs arrivent sur la page
          </small>
        </div>

        {/* Tab Navigation */}
        <div className="gallery-tabs" style={{
          display: 'flex',
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '20px'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: activeTab === tab.id ? '#3b82f6' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#6b7280',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontWeight: activeTab === tab.id ? '600' : '400',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              <i className={tab.icon}></i>
              {tab.text}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <div className="gallery-tab-content">
          {activeTab === 'photos' && (
            <div>
              <AlbumsGalleryAdmin
                albums={albums.length > 0 ? albums : [{ id: 'general', title: 'Galerie principale', photos }]}
                onChange={({ albums: nextAlbums, items: nextPhotos }) => {
                  const current = getContent(fullPath) || galleryData || {};
                  onUpdate(fullPath, { ...current, albums: nextAlbums, photos: nextPhotos });
                }}
              />
            </div>
          )}
          
          {activeTab === 'videos' && (
            <div>
              <AlbumsGalleryAdmin
                albums={
                  videoAlbums.length > 0
                    ? videoAlbums
                    : [{ id: 'general', title: 'Vidéos principales', videos }]
                }
                mediaField="videos"
                galleryType="videos"
                emptyAlbumTitle="Vidéos principales"
                helpText="Organisez vos vidéos par album (Formations, Conférences, Témoignages, etc.)"
                deleteConfirm="Supprimer cet album et toutes ses vidéos ?"
                onChange={({ albums: nextAlbums, items: nextVideos }) => {
                  const current = getContent(fullPath) || galleryData || {};
                  onUpdate(fullPath, {
                    ...current,
                    videoAlbums: nextAlbums,
                    videos: nextVideos,
                  });
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTextInput = (fullPath, value, label, type = 'text') => {
    const supportsHighlight = fullPath.includes('home.hero.title') && !fullPath.includes('subtitle');
    let editableRef = null;

    const handleContentChange = () => {
      if (!editableRef) return;
      let htmlContent = editableRef.innerHTML;
      
      // Convert <div> to <br> for line breaks
      htmlContent = htmlContent.replace(/<div>/gi, '<br>');
      htmlContent = htmlContent.replace(/<\/div>/gi, '');
      
      // Clean up empty <br> at the start
      htmlContent = htmlContent.replace(/^<br\s*\/?>/i, '');
      
      // Convert multiple <br> to single <br>
      htmlContent = htmlContent.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
      
      handleInputChange(fullPath, htmlContent);
    };

    const handleHighlight = () => {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();
      
      if (selectedText) {
        const span = document.createElement('span');
        span.className = 'highlight';
        span.textContent = selectedText;
        span.style.color = '#58C4DC';
        span.style.fontWeight = '600';
        span.style.padding = '0.1em 0.2em';
        span.style.margin = '0';
        range.deleteContents();
        range.insertNode(span);
        
        handleContentChange();
        selection.removeAllRanges();
      }
    };

    const handleRemoveHighlight = () => {
      if (!editableRef) return;
      
      // Get all highlight spans in the editable div
      const highlights = editableRef.querySelectorAll('.highlight');
      
      // Replace each span with its text content
      highlights.forEach(span => {
        const textNode = document.createTextNode(span.textContent);
        span.parentNode.replaceChild(textNode, span);
      });
      
      // Normalize to merge adjacent text nodes
      editableRef.normalize();
      
      // Update the content
      handleContentChange();
    };

    if (supportsHighlight) {
      return (
        <div className="form-group">
          <label className="form-label">
            {label}
          </label>
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            marginBottom: '0.5rem' 
          }}>
            <button
              type="button"
              onClick={handleHighlight}
              style={{
                padding: '0.4rem 0.8rem',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              <i className="fas fa-highlighter"></i>
              Surligner
            </button>
            <button
              type="button"
              onClick={handleRemoveHighlight}
              style={{
                padding: '0.4rem 0.8rem',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              <i className="fas fa-eraser"></i>
              Enlever
            </button>
          </div>
          <div
            ref={(el) => {
              if (el && editableRef !== el) {
                editableRef = el;
                // Only set innerHTML on initial mount, not on every re-render
                if (el.innerHTML === '') {
                  el.innerHTML = value || '';
                }
              }
            }}
            contentEditable
            onInput={handleContentChange}
            onBlur={handleContentChange}
            className="form-input"
            style={{
              minHeight: type === 'textarea' ? '100px' : '40px',
              padding: '0.75rem',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              background: 'white',
              fontSize: '0.95rem',
              lineHeight: '1.5',
              outline: 'none'
            }}
          />
          <small style={{ color: '#28a745', fontSize: '0.75rem', marginTop: '0.3rem', display: 'block' }}>
            ✓ Tapez directement, sélectionnez et cliquez "Surligner" pour mettre en bleu
          </small>
        </div>
      );
    }

    // Regular input for non-highlight fields
    return (
      <div className="form-group">
        <label className="form-label">
          {label}
        </label>
        {type === 'textarea' ? (
          <textarea
            className="form-input textarea"
            value={value || ''}
            onChange={(e) => handleInputChange(fullPath, e.target.value)}
            rows={3}
          />
        ) : (
          <input
            type={type}
            className="form-input"
            value={value || ''}
            onChange={(e) => handleInputChange(fullPath, e.target.value)}
          />
        )}
      </div>
    );
  };

  const renderArrayEditor = (fullPath, array, itemTemplate, hideAddButton = false) => {
    if (!Array.isArray(array)) return null;

    // Detect if this is a stats or buttons array
    const arrayType = fullPath.includes('stats') ? 'stats' : 
                     fullPath.includes('buttons') ? 'buttons' : 
                     'default';

    return (
      <div className="array-editor" data-type={arrayType}>
        {array.map((item, index) => {
          const isStringItem = typeof item === 'string';
          let itemPreview;
          let arrayItemType = arrayType;
          
          if (isStringItem) {
            itemPreview = item.length > 50 ? item.substring(0, 50) + '...' : item;
          } else if (typeof item === 'object' && item !== null) {
            // Create meaningful previews for different object types
            if (arrayType === 'stats') {
              itemPreview = `Stat ${index + 1}`;
            } else if (arrayType === 'buttons') {
              itemPreview = item.text ? `Bouton : ${item.text}` : `Bouton ${index + 1}`;
            } else if (item.name && item.rating) {
              itemPreview = `Avis de ${item.name} (${item.rating}⭐)`;
              arrayItemType = 'reviews';
            } else if (item.date && item.location) {
              itemPreview = `Session ${item.date} - ${item.location}`;
              arrayItemType = 'sessions';
            } else if (item.module && item.title) {
              itemPreview = `${item.module}: ${item.title}`;
              arrayItemType = 'program';
            } else if (item.title && item.formationPhoto) {
              itemPreview = `Formation: ${item.title}`;
              arrayItemType = 'formations';
            } else if (item.title) {
              itemPreview = item.title;
            } else {
              itemPreview = `Item ${index + 1}`;
            }
          } else {
            itemPreview = `Item ${index + 1}`;
          }
          
          const showDeleteButton = arrayType !== 'stats';
          
          return (
            <div key={index} className="array-item" data-type={arrayItemType}>
              <div className="array-item-header">
                <h4>
                  {itemPreview}
                </h4>
                {!isStringItem && arrayType !== 'stats' && arrayType !== 'buttons' && (
                  <button 
                    className="btn-toggle"
                    onClick={() => toggleExpanded(`${fullPath}.${index}`)}
                    title={expandedItems[`${fullPath}.${index}`] ? 'Réduire' : 'Développer'}
                  >
                    <i className={`fas fa-chevron-${expandedItems[`${fullPath}.${index}`] ? 'up' : 'down'}`}></i>
                    <span className="toggle-text">
                      {expandedItems[`${fullPath}.${index}`] ? 'Réduire' : 'Développer'}
                    </span>
                  </button>
                )}
                {showDeleteButton && (
                  <button 
                    className="btn-remove"
                    onClick={() => {
                      const newArray = array.filter((_, i) => i !== index);
                      handleInputChange(fullPath, newArray);
                    }}
                    title="Supprimer l'élément"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                )}
              </div>
              
              {isStringItem ? (
                <div className="array-item-content">
                  <input
                    type="text"
                    className="form-input"
                    value={item}
                    onChange={(e) => {
                      const newArray = [...array];
                      newArray[index] = e.target.value;
                      handleInputChange(fullPath, newArray);
                    }}
                    placeholder="Saisir le texte..."
                  />
                </div>
              ) : (
                (expandedItems[`${fullPath}.${index}`] !== false || arrayType === 'stats' || arrayType === 'buttons') && (
                  <div className="array-item-content">
                    {renderObjectEditor(`${fullPath}.${index}`, item)}
                  </div>
                )
              )}
            </div>
          );
        })}
        {!hideAddButton && (
          <button 
            className="btn-add-item"
            onClick={() => {
              const newArray = [...array, itemTemplate];
              handleInputChange(fullPath, newArray);
            }}
          >
            <i className="fas fa-plus"></i>
            Ajouter un élément
          </button>
        )}
      </div>
    );
  };

  const renderObjectEditor = (fullPath, obj) => {
    if (!obj || typeof obj !== 'object') return null;

    // Special handling for expertGallery - section settings + tabbed gallery
    if (isExpertGallery(fullPath)) {
      const actions = obj.actions || [];
      return (
        <div className="object-editor">
          <div className="form-group">
            <label className="form-label">Titre de la section</label>
            <input
              type="text"
              className="form-input"
              value={obj.title || ''}
              onChange={(e) => handleInputChange(`${fullPath}.title`, e.target.value)}
              placeholder="Notre institut en images"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Sous-titre</label>
            <textarea
              className="form-input"
              value={obj.subtitle || ''}
              onChange={(e) => handleInputChange(`${fullPath}.subtitle`, e.target.value)}
              placeholder="Instants de formation..."
              rows={2}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Onglet par défaut</label>
            <select
              className="form-input"
              value={obj.defaultTab || 'photos'}
              onChange={(e) => handleInputChange(`${fullPath}.defaultTab`, e.target.value)}
            >
              <option value="photos">Photos</option>
              <option value="videos">Vidéos</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Galerie Photos & Vidéos</label>
            <TabbedGallery
              fullPath={fullPath}
              galleryData={obj}
              onUpdate={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Boutons d'action</label>
            {actions.map((action, index) => (
              <div key={index} className="nested-object" style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <div className="form-group">
                  <label>Texte</label>
                  <input type="text" value={action.text || ''} onChange={(e) => handleInputChange(`${fullPath}.actions.${index}.text`, e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Lien</label>
                  <input type="text" value={action.link || ''} onChange={(e) => handleInputChange(`${fullPath}.actions.${index}.link`, e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Icône</label>
                  <input type="text" value={action.icon || ''} onChange={(e) => handleInputChange(`${fullPath}.actions.${index}.icon`, e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <input type="text" value={action.type || ''} onChange={(e) => handleInputChange(`${fullPath}.actions.${index}.type`, e.target.value)} placeholder="primary" />
                </div>
                <button type="button" className="btn-delete" onClick={() => handleInputChange(`${fullPath}.actions`, actions.filter((_, i) => i !== index))}>
                  <i className="fas fa-trash"></i> Supprimer
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-add-item"
              onClick={() => handleInputChange(`${fullPath}.actions`, [...actions, { text: '', link: '', type: 'outline', icon: '', target: '_blank' }])}
            >
              <i className="fas fa-plus"></i> Ajouter un bouton
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="object-editor">
        {Object.entries(obj).map(([key, value]) => {
          const currentPath = `${fullPath}.${key}`;
          
          if (typeof value === 'string') {
            // Check if this is the hero video field
            if (isVideoField(key) && isHeroSection(fullPath)) {
              return (
                <div key={key} className="form-group">
                  <HeroVideoAdmin
                    videoUrl={value}
                    onChange={(newValue) => handleInputChange(currentPath, newValue)}
                  />
                </div>
              );
            }
            
            // Check if this is an image field
            if (isImageField(key)) {
              return (
                <div key={key} className="form-group">
                  <label className="form-label">
                    {translateFieldLabel(key)}
                  </label>
                  <ImageUpload
                    value={value}
                    onChange={(newValue) => handleInputChange(currentPath, newValue)}
                    label={translateFieldLabel(key)}
                    placeholder={`Télécharger ${key.toLowerCase().includes('instructor') ? 'photo du formateur' : 'photo de formation'}`}
                  />
                </div>
              );
            }
            
            const isLongText = value.length > 100 || value.includes('\n');
            return renderTextInput(
              currentPath, 
              value, 
              translateFieldLabel(key),
              isLongText ? 'textarea' : 'text'
            );
          }
          
          if (typeof value === 'number') {
            return renderTextInput(
              currentPath, 
              value, 
              translateFieldLabel(key),
              'number'
            );
          }
          
          if (Array.isArray(value)) {
            // Create appropriate template based on array content type and field name
            let template;
            if (value.length > 0) {
              const firstItem = value[0];
              if (typeof firstItem === 'string') {
                template = '';
              } else if (typeof firstItem === 'number') {
                template = 0;
              } else if (typeof firstItem === 'object') {
                template = JSON.parse(JSON.stringify(firstItem));
              } else {
                template = firstItem;
              }
            } else {
              // Create default templates for specific formation fields
              if (key === 'sessions') {
                template = {
                  date: '',
                  location: '',
                  spots: ''
                };
              } else if (key === 'reviews') {
                template = {
                  name: '',
                  rating: 5,
                  comment: '',
                  date: ''
                };
              } else if (key === 'program') {
                template = {
                  module: '',
                  title: '',
                  duration: '',
                  content: []
                };
              } else if (key === 'objectives' || key === 'level') {
                template = '';
              } else if (key === 'formations') {
                template = {
                  id: '',
                  title: '',
                  subtitle: '',
                  icon: '',
                  instructor: '',
                  level: [],
                  description: '',
                  color: '',
                  formationPhoto: '',
                  instructorPhoto: '',
                  objectives: [],
                  program: [],
                  reviews: [],
                  sessions: []
                };
              } else if (key === 'buttons') {
                template = { text: '', icon: '', link: '', type: 'primary', target: '_blank' };
              } else if (key === 'actions') {
                template = { text: '', link: '', type: 'outline', icon: '', target: '_blank' };
              } else {
                template = '';
              }
            }
            
            // Check if this is a gallery array (photos or videos)
            if (isGalleryArray(currentPath, key)) {
              return (
                <div key={key} className="form-group">
                  <label className="form-label">
                    {translateFieldLabel(key)}
                  </label>
                  <GalleryManager
                    value={value}
                    onChange={(newValue) => handleInputChange(currentPath, newValue)}
                    type={getGalleryType(key)}
                  />
                </div>
              );
            }

            return (
              <div key={key} className="form-group">
                <div className="form-label-with-action">
                  <label className="form-label">
                    {translateFieldLabel(key)}
                  </label>
                  {(key === 'stats' || key === 'buttons') && (
                    <button 
                      className="btn-add-item btn-add-inline"
                      onClick={() => {
                        const newArray = [...value, template];
                        handleInputChange(currentPath, newArray);
                      }}
                    >
                      <i className="fas fa-plus"></i>
                      Ajouter un élément
                    </button>
                  )}
                </div>
                {renderArrayEditor(currentPath, value, template, key === 'stats' || key === 'buttons')}
              </div>
            );
          }
          
          if (typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="form-group">
                <label className="form-label">
                  {translateFieldLabel(key)}
                </label>
                <div className="nested-object">
                  {renderObjectEditor(currentPath, value)}
                </div>
              </div>
            );
          }
          
          return null;
        })}
      </div>
    );
  };

  // Route specialized editors before null guard so empty CMS keys can be bootstrapped
  if (isTestimonialsSection(contentPath)) {
    return <TestimonialsAdmin contentPath={contentPath} />;
  }
  if (isUpcomingSessionsSection(contentPath)) {
    return <SessionsAdmin contentPath={contentPath} />;
  }
  if (isFeaturedProgramsSection(contentPath)) {
    return <FeaturedProgramsAdmin contentPath={contentPath} />;
  }
  if (isFeaturesHighlightSection(contentPath)) {
    return <FeaturesHighlightAdmin contentPath={contentPath} />;
  }
  if (isServicesSection(contentPath)) {
    return <ServicesAdmin contentPath={contentPath} />;
  }
  if (isValuesSection(contentPath)) {
    return <ValuesAdmin contentPath={contentPath} />;
  }
  if (isTimelineSection(contentPath)) {
    return <TimelineAdmin contentPath={contentPath} />;
  }
  if (isThreeBlocksSection(contentPath)) {
    return <ThreeBlocksAdmin contentPath={contentPath} />;
  }
  if (isExpertsTeamSection(contentPath)) {
    return <ExpertsTeamAdmin contentPath={contentPath} />;
  }
  if (isTestimonialsSubmitSection(contentPath)) {
    return <ContactSectionAdmin contentPath={contentPath} />;
  }
  if (isPageHeaderSection(contentPath)) {
    return <PageHeaderAdmin contentPath={contentPath} />;
  }
  if (contentPath === 'formations.specialties') {
    return <SpecialtiesAdmin contentPath={contentPath} />;
  }
  if (contentPath === 'global.company') {
    return <CompanyAdmin contentPath={contentPath} />;
  }
  if (isContactArraySection(contentPath)) {
    return <ContactSectionAdmin contentPath={contentPath} />;
  }

  if (sectionData === null || sectionData === undefined) {
    return (
      <div className="editor-loading">
        <i className="fas fa-exclamation-triangle"></i>
        <span>Contenu non trouvé</span>
        <div style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666' }}>
          <button 
            onClick={() => window.location.reload()} 
            style={{ padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Recharger la page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-editor">
      <div className="editor-form">
        {renderObjectEditor(contentPath, sectionData)}
      </div>
    </div>
  );
};

export default ContentEditor;
